import type { WithId } from '@medplum/core';
import type { DiagnosticReport, DocumentReference, Observation, ServiceRequest } from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { Badge } from '../ui/badge';
import { DiagnosticReportSection } from './DiagnosticReportSection';
import { getLabOrderName } from './labOrderUtils';
import { LabOrderProgress } from './LabOrderProgress';
import { LabOrderOrdering } from './LabOrderOrdering';
import type { AoeGroup } from './OrderEntryQuestionsSection';
import { type DetailSection, getNote, getReason } from './serviceRequestUtils';

export type LabOrderDetailProps = {
  serviceRequest: WithId<ServiceRequest>;
  /** HG-emitted requisition PDFs linked by HG identifier. */
  requisitionDocuments?: DocumentReference[];
  isLoadingRequisitionDocuments?: boolean;
  /** HG-emitted specimen label PDFs linked by the same HG identifier. */
  specimenLabelDocuments?: DocumentReference[];
  isLoadingSpecimenLabelDocuments?: boolean;
  /**
   * Order Entry Questions responses. HG attaches these per-test on child SRs (sub-orders),
   * not on the parent order, so the orchestrator walks the parent's children and collects
   * one entry per QR with the originating test name.
   */
  aoeGroups?: AoeGroup[];
  isLoadingAoeGroups?: boolean;
  /**
   * Most-recent `DiagnosticReport` linked to the order. The orchestrator queries by
   * `based-on=ServiceRequest/<id>` and passes the first result. Hidden when no DR is linked
   * (e.g. orders that haven't been resulted yet).
   */
  diagnosticReport?: DiagnosticReport;
  /** Resolved Observations referenced from `diagnosticReport.result[]` and `hasMember[]`. */
  diagnosticReportObservations?: Map<string, Observation>;
  isLoadingDiagnosticReport?: boolean;
  /**
   * True when one or more Observation fetches failed. The section renders an inline error
   * instead of a partial table, so the user knows results are incomplete rather than seeing
   * silently-dropped rows.
   */
  hasDiagnosticReportError?: boolean;
  /** When provided, each result row becomes clickable — used to navigate to that lab's detail. */
  onSelectResultObservation?: (observation: Observation) => void;
};

const getSections = (sr: WithId<ServiceRequest>, t: TFunction<'records'>): DetailSection[] =>
  (
    [
      { title: t('orderDate'), value: sr.authoredOn ? formatDateTime(sr.authoredOn) : null },
      { title: t('orderingProvider'), value: sr.requester?.display ?? null },
      { title: t('performingLab'), value: sr.performer?.[0]?.display ?? null },
      { title: t('requisitionId'), value: sr.requisition?.value ?? null },
      { title: t('priority'), value: sr.priority && sr.priority !== 'routine' ? sr.priority : null },
      { title: t('reason'), value: getReason(sr) },
      { title: t('notes'), value: getNote(sr), preserveWhitespace: true },
    ] as { title: string; value: string | null; preserveWhitespace?: boolean }[]
  ).filter((s): s is DetailSection => s.value !== null);

export const LabOrderDetail = ({
  serviceRequest,
  requisitionDocuments,
  isLoadingRequisitionDocuments = false,
  specimenLabelDocuments,
  isLoadingSpecimenLabelDocuments = false,
  aoeGroups,
  isLoadingAoeGroups = false,
  diagnosticReport,
  diagnosticReportObservations,
  isLoadingDiagnosticReport = false,
  hasDiagnosticReportError = false,
  onSelectResultObservation,
}: LabOrderDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const name = getLabOrderName(serviceRequest, t);
  const sections = getSections(serviceRequest, t);

  /*
    Each section renders only when the orchestrator opted into the corresponding query.
    - `undefined` = query was disabled (non-HG orders, missing requisition id) → hide the whole
      section so we don't show "no documents found" placeholders that don't apply.
    - `[]` = query ran and returned no results → still render so the empty-state message tells
      the user the lab hasn't emitted a document yet.
    The AOE and results sections use a stricter gate: they return `null` for the empty/no-DR
    case, so we gate on presence here to avoid rendering an empty section.
  */
  const showRequisition = isLoadingRequisitionDocuments || requisitionDocuments !== undefined;
  const showSpecimenLabel = isLoadingSpecimenLabelDocuments || specimenLabelDocuments !== undefined;
  const showAoe = isLoadingAoeGroups || (aoeGroups !== undefined && aoeGroups.length > 0);
  // Results lead the view. Once a report is in, show the results table; until then, show the
  // order's progress timeline. Order details collapse behind a (default-closed) accordion once
  // results arrive, and start expanded while the order is still in progress.
  const hasResults = isLoadingDiagnosticReport || diagnosticReport !== undefined || hasDiagnosticReportError;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-medium">{name}</h1>
        <Badge variant="outline">{serviceRequest.status ?? t('unknown')}</Badge>
      </div>

      {hasResults ? (
        <DiagnosticReportSection
          diagnosticReport={diagnosticReport}
          observations={diagnosticReportObservations}
          isLoading={isLoadingDiagnosticReport}
          hasError={hasDiagnosticReportError}
          onSelectObservation={onSelectResultObservation}
        />
      ) : (
        <LabOrderProgress serviceRequest={serviceRequest} />
      )}

      <LabOrderOrdering
        sections={sections}
        requisitionDocuments={requisitionDocuments}
        isLoadingRequisitionDocuments={isLoadingRequisitionDocuments}
        showRequisition={showRequisition}
        specimenLabelDocuments={specimenLabelDocuments}
        isLoadingSpecimenLabelDocuments={isLoadingSpecimenLabelDocuments}
        showSpecimenLabel={showSpecimenLabel}
        aoeGroups={aoeGroups}
        isLoadingAoeGroups={isLoadingAoeGroups}
        showAoe={showAoe}
        defaultOpen={!hasResults}
      />
    </div>
  );
};
